/* eslint-disable no-param-reassign */
/* global dataLayer */
import React, { PureComponent, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { differenceInMonths } from 'date-fns';
import NoSSR from 'react-no-ssr';
import MobileDetect from 'mobile-detect';
import { clientErrorTracker, trans, SoyuzFlag } from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-tracking';
import { MediaQuery, CookiesConsumer, FabButton } from '@spotahome/ui-library';

import Footer from '@spotahome/marketplace-common/src/components/Footer';

import Bell from '@spotahome/marketplace-common/src/components/icons/Bell';

import internalLinkingModule from '@spotahome/marketplace-common/src/shapes/internalLinking';
import InternalLinkingModules from '@spotahome/marketplace-common/src/components/internalLinking/InternalLinkingModules';

import cityToggleConfig from '@spotahome/marketplace-common/src/config/cityToggleConfig';

import InternalLinking from '@spotahome/marketplace-common/src/components/internalLinking';
import HeaderContainer from '@spotahome/marketplace-common/src/components/HeaderContainer';
import HomecardMap from '@spotahome/marketplace-common/src/components/HomecardMap';
import RefreshIcon from '@spotahome/marketplace-common/src/components/icons/Refresh';
import GoogleOneTap from '@spotahome/marketplace-common/src/components/GoogleOneTap';

import {
  adaptFiltersFromUrl,
  getDefaultFilters,
  adaptSortByToUrl,
  getActiveFiltersCount
} from '@spotahome/marketplace-common/src/utils/filters';
import AlertButton from '@spotahome/marketplace-common/src/components/AlertButton';

import mapPointShape from '@spotahome/marketplace-common/src/shapes/mapPoint';

import {
  InstanceManager,
  CustomDimensionsManager
} from '@spotahome/marketplace-common/src/tracking';

import debounce from '@spotahome/marketplace-common/src/utils/debounce';

import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';

import {
  setPoiRequest,
  deletePoiRequest
} from '@spotahome/marketplace-common/src/api/poi-client';

import { getMarkersQuery } from '@spotahome/marketplace-common/src/graphql/queries/markers';

import { getCancellationPoliciesQuery } from '@spotahome/marketplace-common/src/graphql/queries/cancellationPolicy';

import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';

import { removeSeoFilters } from '@spotahome/marketplace-common/src/utils/seo-mode';

import { shiftMarkers } from '../../utils/markerShifter';

import poiShape from '../../shapes/poi';

import { LOAD_MAP_COOKIE, LOAD_MAP_COOKIE_VALUE } from '../../utils/static-map';

import AppliedFiltersModal from '../AppliedFiltersModal';
import Paginator from '../Paginator';
import SEOContent from '../SEOContent';
import SearchBanner from '../SearchBanner';
import SearchTitle from '../SearchTitle';
import FiltersHeader from '../FiltersHeader';
import FiltersExposed from '../FiltersExposed';

import PreFilters from '../PreFilters';
import HomeCardList from '../HomeCardList';
import MapListSwitcher from '../MapListSwitcher';
import Filters from '../Filters';
import MapPlaceholder from '../MapPlaceholder';

import SearchBreadcrumbs from '../SearchBreadcrumbs';

import { HighlightMarkersConsumer } from '../../context/HighlightMarkersContext';

import { mustShowPrefilters } from '../PreFilters/utils/prefilters';
import { PREFILTERS_CONFIG } from '../PreFilters/constants';

import CardPaginator from '../../lib/card-paginator';

import ValueProposition from '../ValueProposition';
import RemoveFilters from '../RemoveFilters/RemoveFilters';

const Map = lazy(() => import('../Map'));
const PlacesAutocomplete = lazy(() =>
  import('@spotahome/marketplace-common/src/components/PlacesAutocomplete')
);

// If a POI is defined, the list of markers will be retrieved ordered by distance
// and the frontend will order by relevance each set defined by this const.
const MAP_ID = 'themap';
const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';
const PLACE_SELECTED_DIFFERENT_CITY_EVENT_CATEGORY =
  'interactive-map-search-different-city';
const HOTJAR_POTENTIAL_PERMANENT_BOOKING = 'potential_permanent_booking';

const VIEW_MAP_LIST_EVENT_CATEGORY = 'Search';
const VIEW_MAP_LIST_EVENT_ACTION = 'View map-list toggle';
const VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG = 'View map';
const VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG = 'View list';

const INITIAL_STATE = {
  maxTotalResults: 0,
  isFiltersHidden: true,
  canUpdateBoundaries: false
};

const { isMobileScreen, isPhabletScreen, isTabletScreen } = MediaQuery;

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  new MobileDetect(window.navigator.userAgent).phone();

const isMobileOrTablet = () =>
  isMobileScreen() || isPhabletScreen() || isTabletScreen();

const serializeGaParameters = (cityId, serializeArguments) =>
  [
    cityId,
    serializeArguments['move-in'],
    serializeArguments['move-out'],
    serializeArguments.budget,
    serializeArguments['type[]'].join(),
    serializeArguments['topology[]'].join(),
    serializeArguments['bathrooms[]'].join(),
    serializeArguments.bed,
    serializeArguments['features[]'].join(),
    serializeArguments.poi,
    serializeArguments.totalHomecards,
    serializeArguments.totalHomecardsWithIB,
    serializeArguments.verified,
    '',
    '',
    '',
    serializeArguments.instantBooking,
    serializeArguments['areaId[]'].join(),
    serializeArguments.flexibleMoveIn,
    serializeArguments.flexibleMoveOut,
    serializeArguments.plan,
    serializeArguments.rentalType,
    serializeArguments.isBestChoice
  ].join(';');

const sendCityDateEvent = (cityId, parameters) => {
  const gaParameters = serializeGaParameters(cityId, parameters);
  Metrics.ga.sendEventWithClientId('dwh-city-date', gaParameters, {
    nonInteraction: true
  });
};

const isPoiFilled = poi => poi && poi.lat && poi.lng;

const sendPageView = (filters, locationString) => {
  const url = AdUrlService.getSearchUrlFromFilters(locationString, filters);

  Metrics.ga.set('page', url);
  Metrics.ga.sendPageView();
};

const pushDataLayerProductList = (markersPage, moveIn, moveOut) => {
  if (typeof dataLayer !== 'undefined') {
    const productIdList = markersPage.slice(0, 3).map(ru => ru.id);

    dataLayer.push({
      PageType: 'Listingpage',
      HashedEmail: '',
      ProductIDList: productIdList,
      din: moveIn,
      dout: moveOut
    });
  }
};

const parseFlexibleMoveFilters = ({
  flexibleMoveIn,
  flexibleMoveOut,
  ...other
}) => ({
  ...other,
  flexibleMoveIn: flexibleMoveIn === 0 ? '0' : false,
  flexibleMoveOut: flexibleMoveOut === 0 ? '0' : false
});
class Search extends PureComponent {
  requestNewContent = debounce(async ({ shouldUpdateMarkers }) => {
    let numberMarkers = this.state.numberOfMarkers;
    const { locationString, city, sortingAlgorithm } = this.props;

    const adaptedFilters = this.getAdaptedFilters();
    if (shouldUpdateMarkers) {
      numberMarkers = (await this.getMarkers()).numberMarkers;
    }

    this.cdManager.setSearchQueryCustomDimensions(
      city.id,
      adaptedFilters,
      numberMarkers
    );

    InstanceManager.getClientSideInstance().sendEvent({
      eventCategory: 'Search',
      eventAction: 'user search',
      eventLabel: 'apply filters'
    });

    const currentFilters = adaptSortByToUrl(
      this.state.filters,
      sortingAlgorithm
    );

    sendPageView(currentFilters, locationString);
  }, 250);

  constructor(props) {
    super(props);

    const {
      initialFiltersFromQuery,
      seoMode,
      sessionPoi,
      initialMarkerList,
      numberOfMarkers,
      itemsPerPage,
      loadMap
    } = props;

    const filters = {
      ...getDefaultFilters(),
      ...initialFiltersFromQuery,
      seoMode
    };

    let pointOfInterest = null;

    if (filters.sortBy === 'POI' && filters.poiLat && filters.poiLng) {
      pointOfInterest = {
        lat: filters.poiLat,
        lng: filters.poiLng,
        name: filters.poiName || '',
        placeId: filters.poiId || ''
      };
      if (
        isPoiFilled(sessionPoi) &&
        pointOfInterest.lat === sessionPoi.lat &&
        pointOfInterest.lng === sessionPoi.lng
      ) {
        pointOfInterest = sessionPoi;
      }
    } else if (isPoiFilled(sessionPoi)) {
      pointOfInterest = sessionPoi;
    }

    filters.map = this.props.map;

    this.state = {
      ...INITIAL_STATE,
      paginatedMarkers: initialMarkerList,
      numberOfMarkers,
      filters,
      pointOfInterest,
      isMapLoadRequested: loadMap
    };

    this.cardPaginator = new CardPaginator({ pageSize: itemsPerPage });

    this.cardPaginator.initializeData(this.state.paginatedMarkers);
  }

  componentDidMount() {
    const { filters, pointOfInterest, paginatedMarkers } = this.state;

    const campaign =
      window && new URLSearchParams(window.location.href).get('utm_campaign');

    if (campaign === 'spottyCatCTA') {
      Metrics.ga.sendEvent(
        'Search',
        'SpottyCatCTATotalResults',
        this.props.numberOfMarkers
      );
    }

    this.cdManager = CustomDimensionsManager(
      InstanceManager.getClientSideInstance()
    );

    if (isPoiFilled(pointOfInterest)) {
      this.updatePointOfInterestState(pointOfInterest);
    }

    if (paginatedMarkers.length > 0) {
      pushDataLayerProductList(
        paginatedMarkers,
        filters['move-in'],
        filters['move-out']
      );
    }

    // TO-DO: Check & remove this
    window.addEventListener(
      'resize',
      debounce(
        () =>
          this.setState(({ filters: prevFilters }) => ({
            filters: {
              ...prevFilters
            }
          })),
        250
      )
    );

    if (!this.props.avoidMarkersRequest) {
      this.getMarkers();
    }

    if (filters['move-in']) {
      this.getCancellationPolicies(filters['move-in'], this.props.city.id);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevFilters = prevState.filters;

    if (this.state.isFiltersHidden !== prevState.isFiltersHidden) {
      document.querySelector('body').style.overflow = this.state.isFiltersHidden
        ? 'auto'
        : 'hidden';
    }

    if (JSON.stringify(this.state.filters) !== JSON.stringify(prevFilters)) {
      const cityId = this.props.city.id;
      const initialHomecards = Object.values(prevProps.initialHomecards);
      const totalHomecards = initialHomecards.length;

      const totalHomecardsWithIB = initialHomecards.filter(
        homecard => homecard.isInstantBooking
      ).length;

      const serializeArguments = {
        ...this.state.filters,
        totalHomecards,
        totalHomecardsWithIB
      };

      sendCityDateEvent(cityId, serializeArguments);

      const prevFiltersAdapted = adaptSortByToUrl(
        prevFilters,
        this.props.sortingAlgorithm
      );

      const shouldUpdateMarkers = this.shouldUpdateMarkers(prevFiltersAdapted);

      this.requestNewContent({ shouldUpdateMarkers });

      const checkInDate = this.state.filters['move-in'];
      if (checkInDate && checkInDate !== prevFilters['move-in']) {
        this.getCancellationPolicies(checkInDate, cityId);
      }
    }
  }

  handlePlaceSelected = async place => {
    const cityId = this.props.city.id;
    if (!place) {
      await deletePoiRequest(cityId);
    } else {
      const { name, lat, lng, cityId: newPoiCityId } = place;

      if (name && lat && lng) {
        Metrics.ga.sendEvent(
          PLACE_SELECTED_EVENT_CATEGORY,
          name,
          `${lat},${lng}`
        );
        Metrics.ga.sendEvent('Search', 'sort-by', 'distance');
      }

      if (newPoiCityId && cityId !== newPoiCityId) {
        Metrics.ga.sendEvent(
          PLACE_SELECTED_DIFFERENT_CITY_EVENT_CATEGORY,
          `currentCity:${cityId}`,
          newPoiCityId
        );
      }

      try {
        await setPoiRequest(cityId, place);
      } catch (error) {
        console.error(error);
      }
    }

    this.updatePointOfInterestState(place);
  };

  handleNextButtonClick = () => {
    Metrics.ga.sendEvent('Search', 'Paginator click', 'Next');
    if (this.cardPaginator.getNextPageNumber()) {
      this.cardPaginator.nextPage();
      this.onNewPageContent();
      this.scrollToTopOfHomecardList();
    }
  };

  handlePreviousButtonClick = () => {
    Metrics.ga.sendEvent('Search', 'Paginator click', 'Prev');
    if (this.cardPaginator.getPreviousPageNumber()) {
      this.cardPaginator.previousPage();
      this.onNewPageContent();
      this.scrollToTopOfHomecardList();
    }
  };

  handleMapWillUnmount = () => {
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  handleMapDidMount = map => {
    this[MAP_ID] = map;
    this[MAP_ID].centerMap = () =>
      map.setCenter(this.props.locationCoordinates);
    this.boundariesFilter = elem =>
      map.coordsInBounds({
        lat: elem.coord[1],
        lng: elem.coord[0]
      });
  };

  handleToggleActiveView = () => {
    this.setState(({ filters }) => {
      const toggled = !filters.map;
      this.sendViewMapListToggleEvent(toggled);

      return {
        filters: { ...filters, map: toggled || !isMobileOrTablet() }
      };
    });
  };

  handleSwitcherClick = () => {
    this.handleToggleActiveView();
    Metrics.ga.sendEvent('Search', 'MapListSwitch', 'list click');
  };

  handleSaveSearchMobile = () => {
    Metrics.ga.sendEvent('Search', 'Alert Primary CTA', 'click button');
    Metrics.ga.sendEvent('Search', 'SaveMySearchMobile', 'click');
  };

  handleOnFiltersToggleClick = () => {
    this.setState(({ isFiltersHidden }) => ({
      isFiltersHidden: !isFiltersHidden
    }));
  };

  handleSortByChange = async sortBy => {
    if (this.state.sortBy === 'POI') {
      await this.handlePlaceSelected(null);
    }
    this.handleFiltersUpdate({ sortBy });
  };

  handleRefreshMapBoundaries = () => {
    this.setState({ newMapBoundaries: false });
    this.cardPaginator.filter(this.boundariesFilter);
    this.onNewPageContent();
  };

  handleMapNewBounds = (isMapVariant = false) => {
    this.setState({ newMapBoundaries: true });
    if (
      isMapVariant ||
      (this.state.canUpdateBoundaries && !isMobileOrTablet())
    ) {
      this.handleRefreshMapBoundaries();
    }
  };

  handleMapZoomChange = bounds => {
    const { northEast, southWest } = bounds;
    const totalMarkersShown = this.cardPaginator.getLength();

    this.cdManager.setSearchMapCustomDimensions(
      `${northEast.lat}_${northEast.lng}`,
      `${southWest.lat}_${southWest.lng}`,
      totalMarkersShown
    );
    const label = `${northEast.lat},${northEast.lng},${southWest.lat},${southWest.lng};${totalMarkersShown}`;

    InstanceManager.getClientSideInstance().sendEvent({
      eventCategory: 'Search',
      eventAction: 'map bounds changed',
      eventLabel: label
    });
  };

  handleFiltersUpdate = filtersToBeMerged => {
    const updatedFilters = Object.assign(
      {},
      this.state.filters,
      filtersToBeMerged
    );

    const newState = Object.assign({}, this.state, {
      filters: removeSeoFilters(updatedFilters)
    });

    // remove rooms when user deselect apartments
    if (
      filtersToBeMerged['type[]'] &&
      !filtersToBeMerged['type[]'].includes('apartments')
    ) {
      newState.filters['topology[]'] = [];
    }

    this.setState(newState);

    this.triggerHotJarEventForLongStays(newState.filters);
  };

  handleClearFilters = () => {
    if (!isMobileDevice() && this[MAP_ID] !== undefined) {
      this[MAP_ID].centerMap();
    }
    this.handleFiltersUpdate(getDefaultFilters());
  };

  handleCloseFilters = () => {
    if (!this.state.isFiltersHidden) {
      this.setState({
        isFiltersHidden: true
      });
      Metrics.ga.sendEvent('Search', 'closeFilters');
    }
  };

  handleNoDepositNavigation = () => {
    this.handleFiltersUpdate({ noDeposit: 1 });
  };

  handleMapLazyLoad = () => {
    this.setState({ isMapLoadRequested: true });
    Metrics.ga.sendEvent('Search', 'MapPlaceholderCTA', 'click');
  };

  onNewPageContent = () => {
    this.setState(this.getOnNewPageContentState, () => {
      this.updateURL(this.state.filters);
    });
  };

  getOnNewPageContentState = prevState => ({
    paginatedMarkers: this.cardPaginator.getCurrentPageContent(),
    filters: {
      ...prevState.filters,
      page: this.cardPaginator.getCurrentPageNumber()
    },
    newMapBoundaries: false
  });

  getAdaptedFilters = () =>
    adaptFiltersFromUrl(this.state.filters, this.props.sortingAlgorithm);

  getCancellationPolicies = async (checkInDate, cityId) => {
    try {
      const { data: cancellationPolicies } = await getCancellationPoliciesQuery(
        getGqlClient()
      )(checkInDate, cityId);
      this.setState({ ...cancellationPolicies });
    } catch {
      this.setState({ cancellationPolicies: {} });
    }
  };

  getMarkers = async () => {
    let newMarkers = [];
    let shiftedMarkers = [];
    let numberOfMarkers = this.props.numberOfMarkers;
    try {
      const { data } = await getMarkersQuery(getGqlClient())(
        this.props.city.id,
        this.getAdaptedFilters()
      );

      newMarkers = data.search.markers;
      numberOfMarkers = newMarkers.length;

      shiftedMarkers = shiftMarkers(newMarkers);
      this.cardPaginator.initializeData(shiftedMarkers);
      this.cardPaginator.filter(this.boundariesFilter);

      this.setState(
        ({ maxTotalResults, filters }) => ({
          maxTotalResults: Math.max(maxTotalResults, numberOfMarkers),
          numberOfMarkers,
          canUpdateBoundaries: true,
          hasInstantBooking: shiftedMarkers.some(
            marker => marker.instantBooking
          ),

          ...this.getOnNewPageContentState({ filters })
        }),
        () => this.updateURL(this.state.filters)
      );
      return {
        numberMarkers: newMarkers.length
      };
    } catch (error) {
      if (error.message !== 'CANCELED') {
        Metrics.hotjar.recordTag('ErrorGetMarkersSearch');
        clientErrorTracker.error({ error, source: 'getMarkersSearch-Error' });
      }
    }

    return {
      numberMarkers: this.state.numberOfMarkers
    };
  };

  getCurrentPage = () => this.cardPaginator.getCurrentPageNumber();

  sendViewMapListToggleEvent = isMapVisible => {
    Metrics.ga.sendEvent(
      VIEW_MAP_LIST_EVENT_CATEGORY,
      VIEW_MAP_LIST_EVENT_ACTION,
      isMapVisible
        ? VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG
        : VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG
    );
  };

  updatePointOfInterestState = point => {
    let pointOfInterest = null;
    let sortBy;
    let poi = {
      poiLat: '',
      poiLng: '',
      poiName: '',
      poiId: '',
      sortBy
    };

    if (point) {
      pointOfInterest = point;
      sortBy = 'POI';
      poi = {
        poiLat: point.lat,
        poiLng: point.lng,
        poiName: point.name,
        poiId: point.placeId,
        sortBy
      };
    }

    this.handleFiltersUpdate(poi);
    this.setState({ pointOfInterest, sortBy });
  };

  updateURL = filters => {
    AdUrlService.setSearchLocationUrl(
      this.props.locationString,
      parseFlexibleMoveFilters(filters)
    );
  };

  calculateMarkersWithoutBounds = () => {
    this[MAP_ID].centerMap();
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  isPrevButtonDisabled = () =>
    this.cardPaginator.getPreviousPageNumber() === null;

  isNextButtonDisabled = () => this.cardPaginator.getNextPageNumber() === null;

  shouldUpdateMarkers = prevFilters => {
    const currentFilters = this.state.filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => key !== 'page'
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  triggerHotJarEventForLongStays = filters => {
    const {
      'move-in': moveIn,
      'move-out': moveOut,
      'rentalType[]': rentalType
    } = filters;

    const { currentLocale } = this.props;

    const currentCityIsInSpain = cityToggleConfig(this.props.city.id).isInSpain;
    const hasLongTermRentalType = rentalType && rentalType.includes('longTerm');
    const hasLongStayInDates =
      moveIn && moveOut && differenceInMonths(moveOut, moveIn) >= 9;

    if (currentCityIsInSpain && (hasLongTermRentalType || hasLongStayInDates)) {
      Metrics.hotjar.triggerEvent(
        `${HOTJAR_POTENTIAL_PERMANENT_BOOKING}_${currentLocale}`
      );
    }
  };

  shouldShowClearFiltersButton = filters => {
    const activeFiltersCount = getActiveFiltersCount(filters);
    return activeFiltersCount > 0 && this.state.numberOfMarkers !== 0;
  };

  scrollToTopOfHomecardList = () =>
    document.querySelector('.search-listing').scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });

  shouldRenderSeoContent = () =>
    this.state.filters.seoMode &&
    !!this.props.seoContent &&
    !!this.props.seoContent.body;

  renderMapView = () => {
    const { paginatedMarkers } = this.state;
    const { isFooterInSearchPage } = this.props;
    const { map } = this.state.filters;
    return (
      (!isMobileOrTablet() || map) && (
        <NoSSR>
          <section
            className={classNames('search-map ', {
              'search-map--new-layout': isFooterInSearchPage
            })}
          >
            {this.renderMapOverlay()}
            {this.renderMap(paginatedMarkers)}
          </section>
        </NoSSR>
      )
    );
  };

  renderMapListSwitcher = isPositionRight => (
    <MapListSwitcher
      onClickLeft={this.handleSwitcherClick}
      onClickRight={this.handleSwitcherClick}
      isPositionRight={isPositionRight}
    />
  );

  renderMapOverlay = (showSwitcher = false) => {
    const { countryCode, locationCoordinates } = this.props;
    const isMobile = isMobileOrTablet();
    const renderMapListSwitcher = () =>
      isMobile ? this.renderMapListSwitcher(true) : null;

    const { pointOfInterest, newMapBoundaries, filters, isMapLoadRequested } =
      this.state;

    const showAutocompleter = isMobileOrTablet() || isMapLoadRequested;

    return (
      showAutocompleter && (
        <div className="search-map__overlay" data-test="map-overlay">
          <div className={`${isMobile ? 'search-map__filters' : ''}`}>
            <div
              className={classNames('search-map__places--extra-content', {
                'search-map__places': isMobile
              })}
            >
              <Suspense fallback={null}>
                <PlacesAutocomplete
                  flat={isMobile}
                  boundCoords={locationCoordinates}
                  pointOfInterest={pointOfInterest}
                  onPlaceSelected={this.handlePlaceSelected}
                  countryCode={countryCode}
                  renderExtraContent={renderMapListSwitcher}
                />
              </Suspense>
            </div>
            {isMobile && this.renderExposedFilters()}
          </div>

          {isMobile && (
            <FabButton
              className={classNames('search-map__refresh-button-rebranding', {
                'search-map__refresh-button-rebranding--hidden':
                  !newMapBoundaries
              })}
              color="secondary-alt"
              onClick={this.handleRefreshMapBoundaries}
              dataTest="searchHereButton"
              renderContent={() => trans('search.search-here')}
              renderIcon={() => <RefreshIcon />}
            />
          )}

          {(isMobile || showSwitcher) && (
            <HighlightMarkersConsumer>
              {({ markerClickedId }) =>
                markerClickedId && (
                  <HomecardMap
                    data-cy="propertyCard"
                    id={markerClickedId}
                    filters={filters}
                    showPrice
                  />
                )
              }
            </HighlightMarkersConsumer>
          )}
        </div>
      )
    );
  };

  renderMap = markersToUse => {
    const {
      currency,
      clientConfig: { defaultZoom },
      locationCoordinates
    } = this.props;

    const { pointOfInterest, isMapLoadRequested } = this.state;

    const showMap = isMobileOrTablet() || isMapLoadRequested;

    return (
      <HighlightMarkersConsumer>
        {({ handleMarkerClicked, homecardMouseEnterId, markerClickedId }) => {
          return showMap ? (
            <Suspense fallback={null}>
              <Map
                ref={map => (this[MAP_ID] = map)}
                mapId={MAP_ID}
                currencyIsoCode={currency.isoCode}
                centerCoordinates={locationCoordinates}
                defaultZoom={defaultZoom[this.props.city.id]}
                markerList={markersToUse}
                pointOfInterest={pointOfInterest}
                homecardMouseEnterId={homecardMouseEnterId}
                markerClickedId={markerClickedId}
                onMarkerClicked={markerId => {
                  this.setState({ isFiltersHidden: true });
                  handleMarkerClicked(markerId);
                }}
                onMapDidMount={this.handleMapDidMount}
                onMapWillUnmount={this.handleMapWillUnmount}
                onIdle={() => this.handleMapNewBounds()}
                onZoomChange={this.handleMapZoomChange}
                showPrice
                isMapLoadDelayed
              />
            </Suspense>
          ) : (
            <CookiesConsumer>
              {({ setCookie }) => (
                <MapPlaceholder
                  city={this.props.city.id}
                  cityCenterCoords={this.props.locationCoordinates}
                  onButtonClick={() => {
                    setCookie(LOAD_MAP_COOKIE, LOAD_MAP_COOKIE_VALUE);
                    this.handleMapLazyLoad();
                  }}
                />
              )}
            </CookiesConsumer>
          );
        }}
      </HighlightMarkersConsumer>
    );
  };

  renderFloatingAlertCTA = () => {
    const handleSaveSearchClick = this.handleSaveSearchMobile;

    return (
      <div className="quick-alert-mobile__switcher">
        <div className="quick-alert-mobile__floating">
          <AlertButton
            buttonProps={{
              renderIcon: () => <Bell />,
              size: 'medium'
            }}
            cityId={this.props.city.id}
            filters={this.state.filters}
            isUserLogged={this.props.isUserLogged}
            onClick={handleSaveSearchClick}
            dataTest="alert-button-mobile"
          />
        </div>
      </div>
    );
  };

  renderPaginator = () => {
    const { locationString } = this.props;
    const { filters } = this.state;
    const nextIsDisabled = this.isNextButtonDisabled();
    const previousIsDisabled = this.isPrevButtonDisabled();

    const nextPage = AdUrlService.getSearchUrlFromFilters(locationString, {
      ...filters,
      page: this.cardPaginator.getNextPageNumber()
    });

    const nextPageUrl = nextIsDisabled ? '' : nextPage;

    const previousPage = AdUrlService.getSearchUrlFromFilters(locationString, {
      ...filters,
      page: this.cardPaginator.getPreviousPageNumber()
    });

    const previousPageUrl = previousIsDisabled ? '' : previousPage;

    return (
      this.cardPaginator.getTotalPages() > 1 && (
        <Paginator
          previousButtonDisabled={previousIsDisabled}
          nextButtonDisabled={nextIsDisabled}
          onPreviousButtonClick={this.handlePreviousButtonClick}
          onNextButtonClick={this.handleNextButtonClick}
          nextPageUrl={nextPageUrl}
          previousPageUrl={previousPageUrl}
        />
      )
    );
  };

  renderHomecardList = () => {
    const { seoContent, internalLinking } = this.props;
    const hasZeroResults = this.cardPaginator.getOriginalLength() === 0;
    const showAlertCreationReminder =
      hasZeroResults || this.isNextButtonDisabled();

    return (
      <div className="search-listing">
        <div className="search-listing__total-results">
          <SearchBanner
            cityId={this.props.city.id}
            cancellationPolicies={this.state.cancellationPolicies}
            moveIn={this.state.filters['move-in']}
          />
          <SearchTitle
            types={this.state.filters['type[]']}
            topology={this.state.filters['topology[]']}
            cityName={this.props.city.cityName}
            totalResults={this.state.numberOfMarkers}
            currentNeighborhood={this.state.filters['areaId[]']}
            orderBy={this.state.filters.sortBy}
            onSortChange={this.handleSortByChange}
            onClearFilters={this.handleClearFilters}
            showClearFiltersButton={this.shouldShowClearFiltersButton(
              this.state.filters
            )}
          />
        </div>
        {hasZeroResults ? (
          <RemoveFilters
            onFiltersUpdate={this.handleFiltersUpdate}
            filters={this.state.filters}
          />
        ) : null}
        <HomeCardList
          cancellationPolicies={this.state.cancellationPolicies}
          initialHomecards={this.props.initialHomecards}
          markerList={this.state.paginatedMarkers}
          filters={this.state.filters}
          onClearExtendedFilters={this.handleClearFilters}
          calculateMarkersWithoutBounds={this.calculateMarkersWithoutBounds}
          pointOfInterest={this.state.pointOfInterest}
          cityId={this.props.city.id}
          hasZeroResults={hasZeroResults}
          showAlertCreationReminder={showAlertCreationReminder}
          preloadedHomecards={this.props.preloadedHomecards}
          hasLazyLoad
        />
        {this.renderPaginator()}
        <SoyuzFlag name="ShowSearchAndSeoLandingsBreadcrumbs">
          {flag =>
            flag.isOn() ? (
              <SearchBreadcrumbs
                cityId={this.props.city.id}
                propertyType={this.state.filters['type[]']}
                topology={this.state.filters['topology[]']}
              />
            ) : null
          }
        </SoyuzFlag>

        <SoyuzFlag name="searchInternalLinkingModules">
          {flag =>
            flag.isOn() ? (
              <InternalLinkingModules internalLinking={internalLinking} />
            ) : (
              <InternalLinking
                cityId={this.props.city.id}
                pageName="SearchPage"
                propertyTypes={this.state.filters['type[]']}
              />
            )
          }
        </SoyuzFlag>

        {this.shouldRenderSeoContent() && (
          <SEOContent
            title={seoContent.title}
            subtitle={seoContent.subtitle}
            body={seoContent.body}
          />
        )}
      </div>
    );
  };

  renderExposedFilters = () => {
    const { filters } = this.state;
    const totalResults = this.cardPaginator.getOriginalLength();
    const { currency, maxPriceInCity, city, sortingAlgorithm } = this.props;

    return (
      <FiltersExposed
        filters={filters}
        totalResults={totalResults}
        currencyIsoCode={currency.isoCode}
        onFiltersClose={this.handleCloseFilters}
        onFiltersToggle={this.handleOnFiltersToggleClick}
        onFiltersUpdate={this.handleFiltersUpdate}
        maxPriceInCity={maxPriceInCity}
        minimumStaying={city.minimumStaying}
        cityId={city.id}
        sortingAlgorithm={sortingAlgorithm}
      />
    );
  };

  renderPreFilters = () => {
    const { filters } = this.state;

    return (
      mustShowPrefilters(filters, PREFILTERS_CONFIG) && (
        <PreFilters
          filters={filters}
          onFiltersUpdate={this.handleFiltersUpdate}
        />
      )
    );
  };

  renderFullFilters = () => {
    const isFooterInSearchPage = this.props.isFooterInSearchPage;
    return !this.state.isFiltersHidden ? (
      <NoSSR>
        <div
          className={classNames('search-filters-full', {
            'search-filters-full--new-layout': isFooterInSearchPage
          })}
        >
          <Filters
            currency={this.props.currency}
            filters={this.state.filters}
            hasInstantBooking={this.state.hasInstantBooking}
            maxPriceInCity={this.props.maxPriceInCity}
            onFiltersUpdate={this.handleFiltersUpdate}
            cityId={this.props.city.id}
            onClearFilters={this.handleClearFilters}
            onCloseFilters={this.handleCloseFilters}
            totalResults={this.cardPaginator.getOriginalLength()}
          />
        </div>
        <FiltersHeader
          results={this.state.numberOfMarkers}
          citySlug={this.props.city.id}
          totalResults={this.cardPaginator.getOriginalLength()}
          onHandleFiltersToggle={this.handleOnFiltersToggleClick}
          maxTotalResults={this.state.maxTotalResults}
          filters={this.state.filters}
          currency={this.props.currency}
          isFooterInSearchPage={isFooterInSearchPage}
        />
      </NoSSR>
    ) : null;
  };

  render() {
    const { city, isUserLogged, isSafari, isFooterInSearchPage } = this.props;

    const { map } = this.state.filters;

    const isMobileOrTabletOnMapView = map && isMobileOrTablet();

    const renderMapListSwitcher = () =>
      this.renderMapListSwitcher(isMobileOrTabletOnMapView);

    const headerContainerClass = classNames('wrapper-header', {
      'wrapper-header--hidden': isMobileOrTabletOnMapView
    });

    const isCityExcludedFromValueProposition = cityToggleConfig(
      this.props.city?.id
    ).excludeCitiesFromValueProposition;

    return (
      <>
        {!isMobileOrTabletOnMapView && (
          <div className={headerContainerClass}>
            <HeaderContainer renderExtraContent={renderMapListSwitcher} />
            <NoSSR>{this.renderExposedFilters()}</NoSSR>
          </div>
        )}

        <div id="wrapper-search" data-browser={isSafari ? 'safari' : 'other'}>
          <div
            className={`layout-search ${
              isFooterInSearchPage ? 'layout-search--new-layout' : ''
            }`}
          >
            <div className="search-results">
              <div
                className={classNames('search-sidebar', {
                  'search-sidebar--new-layout': isFooterInSearchPage
                })}
              >
                {this.renderFullFilters()}
                <div
                  id="search-scroll"
                  className={classNames('search-scroll', {
                    'search-scroll--new-layout': isFooterInSearchPage
                  })}
                >
                  {this.renderPreFilters()}
                  {isCityExcludedFromValueProposition ? null : (
                    <ValueProposition />
                  )}
                  {this.renderHomecardList()}
                  {this.renderFloatingAlertCTA()}
                </div>
              </div>

              {this.renderMapView()}
              {isMobileOrTabletOnMapView && (
                <>
                  {this.renderFloatingAlertCTA()}
                  {this.renderFullFilters()}
                </>
              )}
            </div>
            {isFooterInSearchPage ? <Footer /> : null}
          </div>
        </div>

        <GoogleOneTap isUserLogged={isUserLogged} />
        <AppliedFiltersModal filters={this.state.filters} cityId={city.id} />
      </>
    );
  }
}

Search.propTypes = {
  locationString: PropTypes.string,
  locationCoordinates: PropTypes.shape({
    lat: PropTypes.string,
    lng: PropTypes.string
  }).isRequired,
  sessionPoi: PropTypes.shape(poiShape),
  city: PropTypes.shape({
    id: PropTypes.string.isRequired,
    cityName: PropTypes.string.isRequired,
    minimumStaying: PropTypes.number.isRequired,
    center: PropTypes.shape(mapPointShape)
  }).isRequired,
  clientConfig: PropTypes.shape({
    defaultZoom: PropTypes.shape({
      [PropTypes.string]: PropTypes.number
    })
  }).isRequired,
  countryCode: PropTypes.string,
  currency: PropTypes.shape({ isoCode: PropTypes.string }),
  maxPriceInCity: PropTypes.number.isRequired,
  initialFiltersFromQuery: PropTypes.shape({}),
  initialMarkerList: PropTypes.arrayOf(PropTypes.object),
  numberOfMarkers: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string,
  itemsPerPage: PropTypes.number.isRequired,
  initialHomecards: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      isInstantBooking: PropTypes.bool
    })
  }).isRequired,
  seoMode: PropTypes.bool,
  seoContent: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    body: PropTypes.string
  }),
  isUserLogged: PropTypes.bool.isRequired,
  currentLocale: PropTypes.string.isRequired,
  map: PropTypes.bool,
  avoidMarkersRequest: PropTypes.bool,
  preloadedHomecards: PropTypes.arrayOf(PropTypes.string).isRequired,
  isSafari: PropTypes.bool.isRequired,
  loadMap: PropTypes.bool.isRequired,
  isFooterInSearchPage: PropTypes.bool,
  internalLinking: PropTypes.arrayOf(PropTypes.shape(internalLinkingModule))
    .isRequired
};

Search.defaultProps = {
  locationString: '',
  sessionPoi: {},
  initialFiltersFromQuery: {},
  sortingAlgorithm: '',
  initialMarkerList: [],
  countryCode: '',
  currency: undefined,
  seoMode: false,
  seoContent: null,
  map: false,
  avoidMarkersRequest: false,
  isFooterInSearchPage: false
};

export default Search;
